import { Box, Stack } from '@web/components/atoms/Layout'
import type { Row as RowProps } from '@web/graphql-components'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { checkIfComponentVisible, ComponentWrapper, PageComponentProps } from '../utils'
import { PropsWithChildren } from 'react'

type Props = RowProps & PageComponentProps & ComponentWrapper

export const SectionInner = (props: PropsWithChildren<Props>) => {
  return (
    <Box
      w="100%"
      h="100%"
      minHeight="inherit"
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: props.mobileStyles?.alignItems ?? 'center',
        justifyContent: props.mobileStyles?.justifyContent ?? 'start',
        '@media screen and (min-width: 901px)': {
          display: 'block',
          alignItems: props.styles?.alignItems ?? 'center',
          justifyContent: props.styles?.justifyContent ?? 'start',
          maxWidth: props.styles?.maxWidth,
        },
      }}
    >
      {props.children}
    </Box>
  )
}

export const Section = (props: PropsWithChildren<Props>) => {
  const styles = props.styles ?? {}
  const desktopBackgroundColor = useColorWithOpacity(styles?.backgroundColor, '1')
  const mobileBackgroundColor = useColorWithOpacity(props.mobileStyles?.backgroundColor, '1', desktopBackgroundColor)

  const mobileStyles = {
    backgroundImage: styles?.backgroundImage,
    backgroundPosition: styles?.backgroundPosition,
    backgroundColor: mobileBackgroundColor,
    opacity: styles?.opacity,
    ...props.mobileStyles,
  }

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      w="100%"
      h="100%"
      minHeight="inherit"
      css={{
        '&:before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
        },
        '@media screen and (max-width: 900px)': {
          [`&${styles?.isFillContent ? ' > .c-row__inner' : ''}:before`]: {
            opacity: mobileStyles?.opacity ?? 0,
            ...(mobileStyles?.backgroundImage
              ? {
                  backgroundImage: `url('${mobileStyles?.backgroundImage}')`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: mobileStyles?.backgroundPosition ?? '50% 50%',
                  backgroundSize: 'cover',
                }
              : {
                  backgroundColor: mobileBackgroundColor,
                }),
          },
        },
        '@media screen and (min-width: 901px)': {
          [`&${styles?.isFillContent ? ' > div' : ''}:before`]: {
            opacity: styles?.opacity ?? 0,
            ...(styles?.backgroundImage
              ? {
                  backgroundImage: `url('${styles?.backgroundImage}')`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: styles?.backgroundPosition ?? '50% 50%',
                  backgroundSize: 'cover',
                }
              : {
                  backgroundColor: desktopBackgroundColor,
                }),
          },
        },
      }}
    >
      <Stack
        alignItems="center"
        className="c-row__inner"
        position="relative"
        gap={0}
        w="100%"
        minHeight="inherit"
        css={{
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
          },
          flexDirection: 'column',
          maxWidth: mobileStyles?.maxWidth
            ? `${mobileStyles.maxWidth}`.includes('px')
              ? mobileStyles.maxWidth
              : `${mobileStyles.maxWidth}px`
            : undefined,
          padding: mobileStyles?.padding,
          aspectRatio: mobileStyles?.aspectRatio,
          '@media screen and (min-width: 900px)': {
            flexDirection: 'row',
            maxWidth: styles?.maxWidth
              ? `${styles.maxWidth}`.includes('px')
                ? styles.maxWidth
                : `${styles.maxWidth}px`
              : undefined,
            padding: styles?.padding,
            aspectRatio: styles?.aspectRatio,
          },
        }}
      >
        {props.children}
      </Stack>
    </Box>
  )
}

const Row = (props: Props) => {
  const childComponents = props.childComponents?.filter((c) => checkIfComponentVisible(c, props.allComponents!))

  const isChildrenHasWidthSet = childComponents?.some((component) => {
    const componentStyles = props.allComponents?.get(component.id)?.styles
    return componentStyles?.width && componentStyles.width !== '100%'
  })

  return (
    <Section {...props}>
      {childComponents?.map((component) => {
        const componentStyles = props.allComponents?.get(component.id)?.styles
        return (
          <SectionInner
            {...props}
            key={component.id}
            styles={{
              ...props.styles,
              maxWidth:
                componentStyles?.width === '100%'
                  ? `${100 / (isChildrenHasWidthSet ? 1 : childComponents!.length)}%`
                  : componentStyles?.width,
            }}
          >
            <props.Component {...component} allComponents={props.allComponents} isWidthSet={true} />
          </SectionInner>
        )
      })}
    </Section>
  )
}

export default Row
